import { apiClient, ApiError } from '../lib/apiClient';
import { readSseEvents } from '../lib/sse';

/**
 * KI-Becken-Mentor API (/api/v1/aquariums/:aquariumId/mentor). Mirrors
 * src/types/mentor.ts and src/controllers/mentor.controller.ts.
 */

export type MentorRole = 'user' | 'assistant';

export interface MentorMessage {
  id: string;
  role: MentorRole;
  content: string;
  createdAt: string;
  /** AI credits the answer cost (assistant messages only). */
  credits: number | null;
  /** Gallery photos sent with the question that produced this answer. */
  photoCount: number;
  /** The answer was cut off at the output limit (ask the mentor to continue). Older rows may omit it. */
  truncated?: boolean;
}

export interface MentorConversationSummary {
  id: string;
  aquariumId: string;
  /** Empty until the first question has been answered. */
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface MentorConversationDetail extends MentorConversationSummary {
  messages: MentorMessage[];
}

export interface MentorReply {
  userMessage: MentorMessage;
  assistantMessage: MentorMessage;
  photoCount: number;
  /** Only in the JSON response. */
  contextTokens?: number;
  /** The answer was cut off at the output limit. */
  truncated?: boolean;
}

/** ApiError.code when the user cancelled the answer (nothing is stored). */
export const MENTOR_STREAM_ABORTED = 'MENTOR_STREAM_ABORTED';
/** ApiError.code when the stream ended without a result (connection lost). */
export const MENTOR_STREAM_INTERRUPTED = 'MENTOR_STREAM_INTERRUPTED';

/** HTTP status an error event stands for (so limit errors are handled like their JSON counterparts). */
function statusOfStreamError(code: string): number {
  if (code === 'AI_BUDGET_EXCEEDED' || code === 'RATE_LIMITED') return 429;
  if (code === 'CONVERSATION_FULL') return 409;
  if (code === 'AI_RESPONSE_INVALID') return 502;
  return 503;
}

export interface StreamMentorOptions {
  /** Answer text as it arrives. */
  onDelta?: (text: string) => void;
  /** Cancels the answer; the promise rejects with ApiError code MENTOR_STREAM_ABORTED. */
  signal?: AbortSignal;
  /** Emergency mode (UC-18): the open incident of this tank the question is about. */
  incidentId?: string;
}

/** Longest accepted question (src/types/mentor.ts MENTOR_MAX_MESSAGE_LENGTH). */
export const MENTOR_MAX_MESSAGE_LENGTH = 2000;

const base = (aquariumId: string): string => `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/mentor/conversations`;

export async function listMentorConversations(aquariumId: string): Promise<MentorConversationSummary[]> {
  const res = await apiClient.get<{ data: MentorConversationSummary[] }>(base(aquariumId));
  return res.data;
}

export async function getMentorConversation(aquariumId: string, conversationId: string): Promise<MentorConversationDetail> {
  const res = await apiClient.get<{ data: MentorConversationDetail }>(`${base(aquariumId)}/${encodeURIComponent(conversationId)}`);
  return res.data;
}

export async function createMentorConversation(aquariumId: string): Promise<MentorConversationDetail> {
  const res = await apiClient.post<{ data: MentorConversationDetail }>(base(aquariumId), {});
  return res.data;
}

export async function deleteMentorConversation(aquariumId: string, conversationId: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/${encodeURIComponent(conversationId)}`);
}

export async function sendMentorMessage(
  aquariumId: string,
  conversationId: string,
  content: string,
  includePhotos: boolean,
): Promise<MentorReply> {
  const res = await apiClient.post<{ data: MentorReply }>(
    `${base(aquariumId)}/${encodeURIComponent(conversationId)}/messages`,
    { content, includePhotos },
  );
  return res.data;
}

/**
 * Sends a question to POST …/messages/stream and reads the SSE answer:
 * `delta` events go to onDelta, `done` resolves with the stored messages,
 * `error` rejects with an ApiError carrying the event's code. Errors before
 * the stream starts (validation, 404, 409, AI limit, rate limit) reject with
 * the ApiError of the JSON response, as every other request.
 */
export async function streamMentorMessage(
  aquariumId: string,
  conversationId: string,
  content: string,
  includePhotos: boolean,
  options: StreamMentorOptions = {},
): Promise<MentorReply> {
  const { onDelta, signal } = options;
  let reply: MentorReply | null = null;
  let failure: ApiError | null = null;
  try {
    const response = await apiClient.streamPost(
      `${base(aquariumId)}/${encodeURIComponent(conversationId)}/messages/stream`,
      { content, includePhotos, ...(options.incidentId ? { incidentId: options.incidentId } : {}) },
      signal ? { signal } : {},
    );
    await readSseEvents(response, (event) => {
      if (reply !== null || failure !== null) return;
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(event.data) as Record<string, unknown>;
      } catch {
        return;
      }
      if (event.event === 'delta' && typeof data.text === 'string') {
        onDelta?.(data.text);
      } else if (event.event === 'done') {
        reply = data as unknown as MentorReply;
      } else if (event.event === 'error') {
        const code = typeof data.code === 'string' ? data.code : 'API_ERROR';
        failure = new ApiError(statusOfStreamError(code), code, typeof data.message === 'string' ? data.message : 'The answer failed', data);
      }
    });
  } catch (err) {
    if (signal?.aborted) throw new ApiError(0, MENTOR_STREAM_ABORTED, 'The answer was cancelled');
    throw err;
  }
  if (failure !== null) throw failure;
  if (reply === null) {
    if (signal?.aborted) throw new ApiError(0, MENTOR_STREAM_ABORTED, 'The answer was cancelled');
    throw new ApiError(0, MENTOR_STREAM_INTERRUPTED, 'The answer stream ended without a result');
  }
  return reply;
}
